import type { ReactNode } from "react";
import type { UISchemaElement } from "@jsonforms/core";
import type { CatalogOption } from "@/lib/api/generated/casedb";
import { CaseFieldReviewTable } from "../field_review/CaseFieldReviewTable";
import { CASE_REVIEW_TABLE_HEAD, CASE_REVIEW_TABLE_HEAD_PLAIN } from "../../constants/caseReviewTable";
import { CaseReviewIcon } from "../field_review/CaseReviewIcon";
import { RecordSection } from "./RecordSection";
import { RecordStrip } from "./RecordStrip";
import { CASE_REVIEW_FILTER_IDS, type CaseReviewFilter } from "../../constants/caseReviewFilters";
import type { CaseReviewCollectionApi } from "../../hooks/useCaseReview";
import { useRecordReviewTable } from "../../hooks/useRecordReviewTable";
import type { SourceHighlightInput } from "../../utils/sourceHighlightDispatch";
export interface RecordTableProps {
  api: CaseReviewCollectionApi;
  /** The layout holding this collection's record detail; the section's own for a top-level list. */
  uischema: UISchemaElement;
  fieldOptions: Record<string, CatalogOption[]>;
  enabled: boolean;
  filter?: CaseReviewFilter;
  search?: string;
  onRequestEdit?: () => void;
  /** A nested collection draws its strip, and the Add button on it, as soon as it has a row. */
  nested?: boolean;
  collapsible?: boolean;
  /**
   * The one group of the record to show, when the section's tabs are the record's groups.
   *
   * <p>The tab has already named the group, so it is not drawn again as a collapsible row.
   */
  activeGroup?: string;
  /** Noneditable facts a specialized section composition may use for titles/grouping. */
  sectionContext?: unknown;
  recordHeader?: ReactNode;
  requestSourceHighlight?: (payload: SourceHighlightInput) => void;
}


export function RecordTable({ api, uischema, fieldOptions, enabled, filter = CASE_REVIEW_FILTER_IDS.all, search = "",
  onRequestEdit, nested = false, collapsible = false, activeGroup, requestSourceHighlight, recordHeader }: RecordTableProps) {
  // An enabled table is already in edit mode, so it passes no request-to-edit down: a readOnly
  // field's empty row must show the plain marker, not an offer to start editing (D114). Enforced
  // here as well as at the workspace because `enabled` and `onRequestEdit` arrive as independent
  // props and nothing else stops a caller supplying both.
  const view = useRecordReviewTable(api, uischema, fieldOptions, filter, search,
    enabled ? undefined : onRequestEdit, activeGroup);
  // An empty nested list offers its own Add button below; once it has a row, the strip carries it.
  const showStrip = !nested || api.rows.length > 0;
  const strip = showStrip && <RecordStrip api={api} optionLabel={view.optionLabel} counts={view.countsOf} enabled={enabled} showDisabledAdd={collapsible} />;
  if (view.error) return <p role="alert">{view.error}</p>;
  if (!view.row || !view.context || !view.schema || !view.layout) {
    const emptyErrors = api.emptyErrors();
    return <RecordSection label={view.spec.labels.records} collapsible={collapsible}>
      {strip}
      {collapsible && !enabled && <p className="cr-record-note">Choose Edit case to add {view.spec.labels.records.toLowerCase()}.</p>}
      <p className="cr-records-empty">{view.spec.labels.empty}</p>
      {emptyErrors.map((message) => <p key={message} className="cr-error" role="alert">{message}</p>)}
      {enabled && api.spec.canAdd !== false && !showStrip && <button type="button" className="cr-record-chip-add" onClick={api.add}>
        <CaseReviewIcon name="plus" />{view.spec.labels.add}</button>}
    </RecordSection>;
  }
  const childSlots = Object.fromEntries((view.children ?? []).map((child) => [child.spec.scope,
    <RecordTable key={child.spec.collectionId} api={child.api} uischema={view.layout!}
      fieldOptions={fieldOptions} enabled={enabled} filter={filter} search={search} onRequestEdit={onRequestEdit}
      requestSourceHighlight={requestSourceHighlight} nested />]));
  return <RecordSection label={view.spec.labels.records} collapsible={collapsible}>
    {strip}
    {collapsible && !enabled && <p className="cr-record-note">Choose Edit case to add {view.spec.labels.records.toLowerCase()}.</p>}
    {recordHeader}
    <div className="cr-section" role="table" aria-label={`${view.spec.labels.item} fields`} data-ai={view.hasAi}>
      {!nested && <div className="cr-table-head" role="row">
        {(view.hasAi ? CASE_REVIEW_TABLE_HEAD : CASE_REVIEW_TABLE_HEAD_PLAIN).map((head) =>
          <span key={head} role="columnheader">{head}</span>)}
      </div>}
      <CaseFieldReviewTable schema={view.schema} uischema={view.shown!} values={view.values as never}
        enabled={enabled} onValidation={(errors) => api.validate(view.row!.recordId, errors)}
        context={{ ...view.context, childSlots, requestSourceHighlight }} />
    </div>
    {!!view.unattached && <p className="cr-record-note" title="The extraction did not say which row these belong to.">
      {view.unattached} AI {view.unattached === 1 ? "value" : "values"} could not be placed
    </p>}
  </RecordSection>;
}
